import { useRequiredContext } from "@redotech/react-util/context";
import AlertCircleIcon from "@redotech/redo-icons/alert-circle.svg";
import AlertCircleFilledIcon from "@redotech/redo-icons/alert-circle_filled.svg";
import CreditCard01Icon from "@redotech/redo-icons/credit-card-01.svg";
import CreditCard01FilledIcon from "@redotech/redo-icons/credit-card-01_filled.svg";
import CurrencyDollarIcon from "@redotech/redo-icons/currency-dollar-circle.svg";
import CurrencyDollarFilledIcon from "@redotech/redo-icons/currency-dollar-circle_filled.svg";
import DashboardIcon from "@redotech/redo-icons/dashboard.svg";
import DashboardFilledIcon from "@redotech/redo-icons/dashboard_filled.svg";
import DataflowIcon from "@redotech/redo-icons/dataflow-03.svg";
import DataflowFilledIcon from "@redotech/redo-icons/dataflow-03_filled.svg";
import PlayCircleIcon from "@redotech/redo-icons/play-circle.svg";
import ShieldDollarIcon from "@redotech/redo-icons/shield-dollar.svg";
import ShieldDollarFilledIcon from "@redotech/redo-icons/shield-dollar_filled.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useState } from "react";
import { useMatch } from "react-router-dom";
import * as modalStyles from "../../../../payments/chargebacks/disputes.module.css";
import { useDisputeAlertsEnrollmentEnabled } from "../../../../payments/chargebacks/use-dispute-alerts-enrollment-enabled";
import { useDisputeResolveEnabled } from "../../../../payments/chargebacks/use-dispute-resolve-enabled";
import * as styles from "./payments-navigation.module.css";

export function usePaymentsNavigation(): PageNavigationEntry | null {
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useContext(TeamContext);

  const disputesEnabled = !!team?.settings.disputes?.enabled;

  const primaryTab: LinkPrimaryTab = {
    id: "payments",
    hasNotification: false,
    UnfilledIcon: CurrencyDollarIcon,
    FilledIcon: CurrencyDollarFilledIcon,
    type: "link",
    href: routes.chargebacksOverviewHref,
    pathRoot: routes.paymentsBaseUrl,
    friendlyName: "Payments",
  };

  const secondaryNavigationData: SecondaryNavigationData = {
    id: "payments",
    name: "Payments",
    NavContent: SecondaryPaymentsNavigation,
  };

  return {
    primaryTab,
    secondaryNavigationData,
    isActiveProduct: disputesEnabled,
  };
}

import {
  RECLAIM_IMAGE_URL,
  RECLAIM_POSTER_URL,
  RECLAIM_VIDEO_URL,
} from "../../../../payments/chargebacks/reclaim-constants";

const SecondaryPaymentsNavigation = memo(
  function SecondaryPaymentsNavigation() {
    const routes = useRequiredContext(MerchantRoutesContext);
    const { enabled: alertsEnabled } = useDisputeAlertsEnrollmentEnabled();
    const { enabled: resolveEnabled } = useDisputeResolveEnabled();
    const [showVideoModal, setShowVideoModal] = useState(false);

    const { id: nodeId, TreeContext } = useNavTreeNode({
      friendlyName: "Payments",
    });

    const chargebacksBaseUrl = routes.chargebacksHref;
    const anyChargebacksChildSelected = useMatch(`${chargebacksBaseUrl}/*`);

    return (
      <TreeContext.Provider value={nodeId}>
        <RedoSecondaryNavItem
          action={{ type: "link", href: routes.chargebacksOverviewHref }}
          anyChildSelected={!!anyChargebacksChildSelected}
          beta
          FilledIcon={ShieldDollarFilledIcon}
          friendlyName="Chargebacks"
          id="chargebacks"
          UnfilledIcon={ShieldDollarIcon}
        >
          <RedoSecondaryNavItem
            action={{ type: "link", href: routes.chargebacksOverviewHref }}
            FilledIcon={DashboardFilledIcon}
            friendlyName="Overview"
            id="chargebacks-overview"
            UnfilledIcon={DashboardIcon}
          />
          <RedoSecondaryNavItem
            action={{ type: "link", href: routes.chargebacksReclaimHref }}
            FilledIcon={CreditCard01FilledIcon}
            friendlyName="Reclaim"
            id="chargebacks-reclaim"
            UnfilledIcon={CreditCard01Icon}
          />
          {alertsEnabled && (
            <RedoSecondaryNavItem
              action={{ type: "link", href: routes.chargebacksAlertsHref }}
              FilledIcon={AlertCircleFilledIcon}
              friendlyName="Alerts"
              id="chargebacks-alerts"
              UnfilledIcon={AlertCircleIcon}
            />
          )}
          {resolveEnabled && (
            <RedoSecondaryNavItem
              action={{ type: "link", href: routes.chargebacksResolveHref }}
              FilledIcon={DataflowFilledIcon}
              friendlyName="Resolve"
              id="chargebacks-resolve"
              UnfilledIcon={DataflowIcon}
            />
          )}
        </RedoSecondaryNavItem>
        {!!anyChargebacksChildSelected && (
          <Flex
            align="center"
            className={styles.videoLauncher}
            cursor="pointer"
            gap="md"
            onClick={() => setShowVideoModal(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setShowVideoModal(true);
              }
            }}
            position="fixed"
            px="lg"
            py="md"
            role="button"
            tabIndex={0}
          >
            <Flex
              align="center"
              borderStyle="solid"
              borderWidth="1px"
              className={styles.videoLauncherIcon}
              justify="center"
              radius="xs"
              shrink={0}
            >
              <PlayCircleIcon height={14} width={14} />
            </Flex>
            <Flex dir="column" gap="xxs">
              <Text
                className={styles.videoLauncherTitle}
                fontSize="xs"
                fontWeight="medium"
              >
                How Reclaim AI works
              </Text>
              <Text className={styles.videoLauncherSubtitle} fontSize="xxs">
                Watch the 2-min video
              </Text>
            </Flex>
          </Flex>
        )}
        {showVideoModal && (
          <RedoModal
            hideFooter
            hideHeader
            isOpen
            modalSize="md"
            muiClassName={modalStyles.backdropBlur}
            noPadding
            onModalCloseRequested={() => setShowVideoModal(false)}
            title="How Reclaim AI works"
          >
            <Flex
              className={styles.videoHero}
              overflow="hidden"
              position="relative"
              topLeftRadius="md"
              topRightRadius="md"
            >
              <img
                alt=""
                className={styles.videoHeroImage}
                src={RECLAIM_IMAGE_URL}
              />
              <Flex className={styles.videoHeroOverlay} position="absolute" />
              <Flex
                className={styles.videoHeroText}
                dir="column"
                gap="xs"
                position="absolute"
              >
                <Text
                  fontFamily="serif"
                  fontSize="5xl"
                  fontWeight="regular"
                  textColor="white"
                >
                  How Reclaim AI works
                </Text>
                <Text className={styles.videoHeroSubtitle} fontSize="sm">
                  Watch the two-minute video
                </Text>
              </Flex>
            </Flex>
            <Flex
              bgColor="primary"
              className={styles.videoPlayerWrap}
              p="2xl"
              position="relative"
              radius="md"
            >
              <video
                className={styles.videoPlayer}
                controls
                playsInline
                poster={RECLAIM_POSTER_URL}
                src={RECLAIM_VIDEO_URL}
              />
            </Flex>
          </RedoModal>
        )}
      </TreeContext.Provider>
    );
  },
);
